import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { getCaptcha, getMe, login } from '../../api/management';
import { Button } from '../../components/ui/Button';
import { Field, inputClassName } from '../../components/ui/Field';

function errorText(error: unknown) {
  return error instanceof Error ? error.message : 'Login failed';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaUrl, setCaptchaUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [error, setError] = useState('');

  const refreshCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const result = await getCaptcha();
      setCaptchaId(result.captchaId);
      setCaptchaUrl(result.imageUrl);
      setCaptchaAnswer('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    void refreshCaptcha();
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!captchaId) return;
    setLoading(true);
    setError('');
    try {
      await login({ username: username.trim(), password, captchaId, captchaAnswer: captchaAnswer.trim() });
      const session = await getMe();
      const requested = searchParams.get('next');
      const safeNext = requested && requested.startsWith('/') && !requested.startsWith('//') ? requested : '/';
      if (session.user.role === 'ADMIN') navigate(safeNext, { replace: true });
      else window.location.replace(safeNext);
    } catch (err) {
      setError(errorText(err));
      if (!(err instanceof ApiError) || err.status !== 429) await refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-slate-100 px-4 py-8">
      <section className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 bg-slate-950 px-6 py-6 text-white">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300">Super Sunday V2</p>
          <h1 className="mt-2 text-2xl font-bold">Admin Login</h1>
          <p className="mt-1 text-sm text-slate-300">Existing backend username, password and captcha are used unchanged.</p>
        </div>

        <form className="space-y-5 p-6" onSubmit={submit}>
          <Field label="Username" required>
            <input className={inputClassName} autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required />
          </Field>
          <Field label="Password" required>
            <div className="relative">
              <input className={`${inputClassName} pr-12`} type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
              <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-1 my-auto grid h-9 w-10 place-items-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-950" aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current stroke-2"><path d="M3 3l18 18"/><path d="M10.6 10.6a2 2 0 002.8 2.8"/><path d="M9.9 4.2A10.7 10.7 0 0112 4c5.5 0 9 5 9 5a15.4 15.4 0 01-3 3.6"/><path d="M6.6 6.6C4.3 8 3 10 3 10s3.5 5 9 5a10.5 10.5 0 004.1-.8"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current stroke-2"><path d="M3 12s3.5-5 9-5 9 5 9 5-3.5 5-9 5-9-5-9-5z"/><circle cx="12" cy="12" r="2.5"/></svg>
                )}
              </button>
            </div>
          </Field>
          <Field label="Captcha" required>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <input className={inputClassName} inputMode="numeric" autoComplete="off" value={captchaAnswer} onChange={(event) => setCaptchaAnswer(event.target.value)} required />
              <button type="button" className="min-w-28 overflow-hidden rounded-lg border border-slate-300 bg-slate-50 px-2" onClick={() => void refreshCaptcha()} disabled={captchaLoading} aria-label="Refresh captcha">
                {captchaUrl ? <img src={captchaUrl} alt="Captcha" className="mx-auto max-h-12 max-w-28" /> : <span className="text-xs text-slate-500">{captchaLoading ? 'Loading…' : 'Refresh'}</span>}
              </button>
            </div>
          </Field>

          {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700" role="alert">{error}</div> : null}
          <Button className="w-full" type="submit" loading={loading} disabled={!captchaId}>Login</Button>
        </form>
      </section>
    </main>
  );
}
